import React, { useRef, useState } from "react";
import { HiX, HiCheck } from "react-icons/hi";

interface AddStepLogFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (stepLog: {
    steps: number;
    notes: string;
    logDate?: string;
  }) => void;
  dateID: string;
  editMode?: boolean;
  initialData?: {
    steps: number;
    notes: string;
  };
  logId?: string;
}

const AddStepLogForm: React.FC<AddStepLogFormProps> = ({
  open,
  onClose,
  onSubmit,
  dateID,
  editMode = false,
  initialData,
  logId,
}) => {
  const [steps, setSteps] = useState(
    initialData?.steps ? initialData.steps.toString() : "",
  );
  const [notes, setNotes] = useState(initialData?.notes || "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Convert between a YYYYMMDD dateID and a YYYY-MM-DD <input type="date"> value
  const dateIDToInputValue = (id: string): string => {
    if (!id || id.length < 8) return "";
    return `${id.substring(0, 4)}-${id.substring(4, 6)}-${id.substring(6, 8)}`;
  };
  const inputValueToDateID = (val: string): string => val.replace(/-/g, "");

  const [dateInput, setDateInput] = useState<string>(
    dateIDToInputValue(dateID),
  );
  const dateInputRef = useRef<HTMLInputElement>(null);

  const openDatePicker = () => {
    const el = dateInputRef.current as
      | (HTMLInputElement & { showPicker?: () => void })
      | null;
    if (!el) return;
    try {
      if (typeof el.showPicker === "function") {
        el.showPicker();
      } else {
        el.focus();
      }
    } catch {
      el.focus();
    }
  };

  const formatDate = (dateID: string) => {
    const year = parseInt(dateID.substring(0, 4));
    const month = parseInt(dateID.substring(4, 6));
    const day = parseInt(dateID.substring(6, 8));
    const date = new Date(year, month - 1, day);

    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleSubmit = async () => {
    const stepCount = parseInt(steps);
    if (isNaN(stepCount) || stepCount <= 0) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        steps: stepCount,
        notes: notes.trim(),
        logDate: inputValueToDateID(dateInput) || dateID,
      });
      handleClose();
    } catch (error) {
      console.error("Error adding step log:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (editMode && initialData) {
      setSteps(initialData.steps.toString());
      setNotes(initialData.notes);
    } else {
      setSteps("");
      setNotes("");
    }
    onClose();
  };

  // Update form values when initial data changes
  React.useEffect(() => {
    if (editMode && initialData) {
      setSteps(initialData.steps.toString());
      setNotes(initialData.notes);
    } else if (!editMode) {
      setSteps("");
      setNotes("");
    }
    setDateInput(dateIDToInputValue(dateID));
  }, [editMode, initialData, open, dateID]);

  const handleStepsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ""); // Only allow digits
    setSteps(value);
  };

  const isValid = steps && parseInt(steps) > 0;

  if (!open) return null;

  const displayDateID = inputValueToDateID(dateInput) || dateID;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/40 backdrop-blur-sm overflow-y-auto"
      style={{ transform: "translateZ(0)" }}
    >
      <div className="flex min-h-screen md:items-center md:justify-center items-start justify-center">
        <div className="w-full max-w-2xl mx-auto overflow-hidden rounded-none md:rounded-md bg-base-100 text-left align-middle shadow-xl min-h-screen md:min-h-0 md:max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-start p-6 flex-shrink-0">
            <div>
              <h2 className="text-lg font-grotesk">
                {editMode ? "Edit Steps" : "Add Steps"}
              </h2>
              {/* Clickable date — opens a native date picker */}
              <div className="relative mt-1">
                <button
                  type="button"
                  onClick={openDatePicker}
                  className="flex items-center gap-1.5 text-sm text-base-content/60 hover:text-base-content transition-colors"
                >
                  <span className="font-medium underline decoration-dotted underline-offset-4">
                    {formatDate(displayDateID)}
                  </span>
                </button>
                <input
                  ref={dateInputRef}
                  type="date"
                  value={dateInput}
                  onChange={(e) => setDateInput(e.target.value)}
                  className="absolute left-0 top-6 w-0 h-0 opacity-0 pointer-events-none"
                  tabIndex={-1}
                  aria-hidden="true"
                />
              </div>
            </div>
            <button
              onClick={handleClose}
              className="bg-base-200 p-2 rounded-full"
            >
              <HiX className="w-5 h-5 text-base-content/80" />
            </button>
          </div>

          {/* Content - Scrollable */}
          <div
            className="flex-1 overflow-y-auto p-6 space-y-6"
            style={{
              WebkitOverflowScrolling: "touch",
              touchAction: "pan-y",
              transform: "translateZ(0)",
            }}
          >
            {/* Steps */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">Step Count</span>
              </label>
              <input
                type="text"
                className="input w-full bg-base-200 text-[16px] sm:text-sm"
                placeholder="10,000"
                value={steps}
                onChange={handleStepsChange}
                inputMode="numeric"
                pattern="[0-9]*"
                autoFocus
              />
            </div>

            {/* Notes */}
            <div className="form-control">
              <textarea
                className="textarea h-32 bg-base-200 resize-none focus:outline-none focus:ring-0 focus:border-transparent text-[16px] sm:text-sm"
                placeholder="Add your notes here... (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          {/* Footer — floats over the scrolling content with a base→transparent fade */}
          <div className="px-5 pb-5 pt-28 -mt-24 flex-shrink-0 relative z-50 flex items-center justify-end gap-3 bg-gradient-to-t from-base-100 from-30% to-transparent pointer-events-none">
            <button
              onClick={handleSubmit}
              disabled={!isValid || isSubmitting}
              aria-label={editMode ? "Update step log" : "Add step log"}
              title={editMode ? "Update step log" : "Add step log"}
              className="pointer-events-auto w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center shadow-lg transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="loading loading-spinner loading-sm" />
              ) : (
                <HiCheck className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddStepLogForm;
